import React, { useContext, useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Rating,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import UserCardRating from "../../../components/UserCardRating";
import AvatarPresence from "../../../components/AvatarPresence";
import socket from "../../../hooks/useSocket";
import { AuthContext } from "../../../context/Auth/AuthContext";

// Valores do enum no backend (libs/socket.ts emite esses números):
// 0 = OFFLINE, 1 = ONLINE, 2 = AWAY
const PRESENCE = { OFFLINE: 0, ONLINE: 1, AWAY: 2 };

const STATUS_OPTIONS = [
  { key: "all", label: "Todos" },
  { key: "active", label: "Ativos" },
  { key: "dayoff", label: "Em folga" },
  { key: "offshift", label: "Fora do plantão" },
];

const SORT_OPTIONS = [
  { key: "total_tickets", label: "Total" },
  { key: "closed_tickets", label: "Fechados" },
  { key: "open_tickets", label: "Em atendimento" },
  { key: "pending_tickets", label: "Pendentes" },
  { key: "total_rating", label: "Nota média" },
];

// Combina dois sinais pra resolver o status visível do atendente:
//   - presença em tempo real (user.status numérico: 0/1/2)
//   - status de trabalho calculado no Dashboard (active/dayoff/offshift)
//
// Folga e fora-do-plantão têm prioridade sobre online/ausente/offline porque
// o usuário pode estar logado em folga, e isso é mais relevante pro supervisor.
//
// O backend faz raw query e às vezes devolve número como string. Usa
// Number(...) pra normalizar e evitar bug do === 1 vs "1".
const resolveUserStatus = (user) => {
  if (user._status === "dayoff") {
    return { key: "dayoff", label: "Em folga", dotColor: "#a855f7", chipColor: "secondary" };
  }
  if (user._status === "offshift") {
    return { key: "offshift", label: "Fora do plantão", dotColor: "#f97316", chipColor: "warning" };
  }
  const presence = Number(user.status);
  if (presence === PRESENCE.ONLINE) {
    return { key: "online", label: "Online", dotColor: "#22c55e", chipColor: "success" };
  }
  if (presence === PRESENCE.AWAY) {
    return { key: "away", label: "Ausente", dotColor: "#facc15", chipColor: "warning" };
  }
  return { key: "offline", label: "Offline", dotColor: "#9ca3af", chipColor: "default" };
};

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const Row = ({ user, position, isMe }) => {
  const total = safeNum(user.total_tickets);
  const open = safeNum(user.open_tickets);
  const pending = safeNum(user.pending_tickets);
  const closed = safeNum(user.closed_tickets);
  const rating = safeNum(user.total_rating);

  const presence = resolveUserStatus(user);
  // Atendentes indisponíveis ficam levemente esmaecidos pra dar contraste
  // visual em relação a quem está online de fato.
  const isAvailable = presence.key === "online" || presence.key === "away";

  // Cor sutil só pro top 3 — número grande chama atenção sem precisar de
  // medalha gigante de ouro/prata/bronze.
  const positionColor =
    position === 1
      ? "primary.main"
      : position <= 3
        ? "text.primary"
        : "text.secondary";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1, sm: 1.5 },
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        opacity: isAvailable ? 1 : 0.72,
        bgcolor: isMe ? (t) => alpha(t.palette.primary.main, 0.05) : "transparent",
        transition: "background-color 120ms ease",
        "&:hover": {
          bgcolor: (t) =>
            isMe ? alpha(t.palette.primary.main, 0.08) : alpha(t.palette.action.hover, 1),
        },
      }}
    >
      <Typography
        sx={{
          width: 24,
          textAlign: "right",
          fontWeight: position <= 3 ? 700 : 500,
          fontSize: position <= 3 ? 14 : 12,
          color: positionColor,
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          flexShrink: 0,
        }}
      >
        {position}
      </Typography>

      {/* Reusa o AvatarPresence do sidebar pra consistência visual com o
          resto do sistema (mesmo Badge animado de "ripple"). */}
      <Box sx={{ flexShrink: 0, display: "inline-flex" }}>
        <AvatarPresence
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          variant="dot"
          status={
            presence.key === "online"
              ? PRESENCE.ONLINE
              : presence.key === "away"
                ? PRESENCE.AWAY
                : PRESENCE.OFFLINE
          }
          userId={user.id}
        >
          <Avatar alt={user.name} src={user.media_url} sx={{ width: 36, height: 36 }} />
        </AvatarPresence>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {user.name}
          </Typography>
          {isMe && (
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: 9,
                color: "primary.main",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                px: 0.6,
                py: 0.1,
                borderRadius: 0.5,
                letterSpacing: 0.5,
                lineHeight: 1.4,
              }}
            >
              VOCÊ
            </Box>
          )}
          {/* Status sempre visível — todo atendente tem um estado. */}
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.4,
              fontWeight: 600,
              fontSize: 10,
              color:
                presence.chipColor === "default"
                  ? "text.secondary"
                  : `${presence.chipColor}.main`,
              bgcolor: (t) =>
                presence.chipColor === "default"
                  ? alpha(t.palette.text.primary, 0.06)
                  : alpha(t.palette[presence.chipColor].main, 0.1),
              px: 0.7,
              py: 0.15,
              borderRadius: 0.75,
              letterSpacing: 0.3,
              lineHeight: 1.4,
              textTransform: "uppercase",
            }}
          >
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: presence.dotColor,
                display: "inline-block",
              }}
            />
            {presence.label}
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", rowGap: 0.25 }}>
          <Tooltip
            title={<UserCardRating user={user} />}
            placement="bottom-start"
            arrow
            componentsProps={{ tooltip: { sx: { bgcolor: "transparent", p: 0 } } }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Rating size="small" value={rating} precision={0.1} readOnly sx={{ fontSize: 13 }} />
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  fontWeight: 600,
                  color: rating > 0 ? "text.primary" : "text.disabled",
                }}
              >
                {rating > 0 ? rating.toFixed(1) : "—"}
              </Typography>
            </Box>
          </Tooltip>

          {/* Breakdown discreto. Só mostra os status que têm contagem > 0
              pra não ficar uma fileira de zeros. */}
          {(open > 0 || pending > 0 || closed > 0) && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11, fontFamily: 'ui-monospace, monospace' }}
            >
              {[
                open > 0 && `${open} aberto${open > 1 ? "s" : ""}`,
                pending > 0 && `${pending} pendente${pending > 1 ? "s" : ""}`,
                closed > 0 && `${closed} fechado${closed > 1 ? "s" : ""}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          )}
        </Stack>
      </Box>

      <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: 36 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 18,
            lineHeight: 1,
            color: position === 1 ? "primary.main" : "text.primary",
          }}
        >
          {total}
        </Typography>
      </Box>
    </Box>
  );
};

const SkeletonRows = ({ count = 6 }) =>
  Array.from({ length: count }, (_, i) => (
    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.25 }}>
      <Skeleton variant="text" width={16} />
      <Skeleton variant="circular" width={36} height={36} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" height={12} />
      </Box>
      <Skeleton variant="text" width={24} />
    </Box>
  ));

const UserRanking = ({ users = [], loading = false, currentUserId }) => {
  const { user: authUser } = useContext(AuthContext);
  const companyId = authUser?.companyId;

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("total_tickets");
  const [sortDir, setSortDir] = useState("desc");

  // Mapa userId -> status numérico. Atualizado em tempo real pelo socket.
  // Sobrescreve o status que veio da query inicial quando há novidade.
  const [liveStatuses, setLiveStatuses] = useState({});

  const deferredSearch = useDeferredValue(search.trim().toLowerCase());

  // Pega o snapshot dos usuários com socket conectado NESTE momento.
  // O status no banco pode estar desatualizado (ex: alguém abriu a aba com JWT
  // válido sem passar pelo /signin) — então pra começar certo, pergunta direto
  // pro Socket.IO quem está conectado e marca esses como ONLINE.
  const fetchOnlineUsers = () => {
    if (!companyId) return;
    socket.emit("get_online_users", { companyId }, (resp) => {
      if (!resp?.userIds) return;
      setLiveStatuses((prev) => {
        // Reseta o estado live com base no snapshot atual do servidor —
        // quem não estiver na lista de conectados fica off-line.
        const next = {};
        resp.userIds.forEach((id) => {
          next[id] = PRESENCE.ONLINE;
        });
        // Preserva status "away" que o usuário possa ter mudado durante a
        // sessão atual e que veio via socket (não está no resp).
        Object.entries(prev).forEach(([id, status]) => {
          if (Number(status) === PRESENCE.AWAY && next[id] === PRESENCE.ONLINE) {
            next[id] = PRESENCE.AWAY;
          }
        });
        return next;
      });
    });
  };

  // Escuta o evento user_status_${companyId} que o backend emite quando
  // alguém faz login, muda de status (online/away) ou desconecta. Mesmo
  // evento que o sidebar já usa — sem precisar criar nada novo.
  useEffect(() => {
    if (!companyId) return;

    fetchOnlineUsers();

    const event = `user_status_${companyId}`;
    const handler = (data) => {
      if (data?.id === undefined || data?.id === null) return;
      setLiveStatuses((prev) => ({ ...prev, [data.id]: Number(data.status) }));
    };
    socket.on(event, handler);

    // Reidrata quando o socket reconecta — sem isso, se o usuário perder a
    // conexão e voltar, o estado fica preso no que tinha antes.
    const onConnect = () => fetchOnlineUsers();
    socket.on("connect", onConnect);

    return () => {
      socket.off(event, handler);
      socket.off("connect", onConnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Aplica o status que chegou via socket (se houver) por cima do snapshot
  // do backend. Garante que mudanças online/offline reflitam na hora.
  const usersWithLiveStatus = useMemo(
    () =>
      users.map((u) =>
        liveStatuses[u.id] !== undefined ? { ...u, status: liveStatuses[u.id] } : u
      ),
    [users, liveStatuses]
  );

  const statusCounts = useMemo(() => {
    const c = { all: usersWithLiveStatus.length, active: 0, dayoff: 0, offshift: 0 };
    usersWithLiveStatus.forEach((u) => {
      if (u._status === "active") c.active++;
      else if (u._status === "dayoff") c.dayoff++;
      else if (u._status === "offshift") c.offshift++;
    });
    return c;
  }, [usersWithLiveStatus]);

  const visible = useMemo(() => {
    let arr = usersWithLiveStatus.slice();

    if (statusFilter !== "all") {
      arr = arr.filter((u) => u._status === statusFilter);
    }
    if (deferredSearch) {
      arr = arr.filter((u) => (u.name || "").toLowerCase().includes(deferredSearch));
    }

    arr.sort((a, b) => {
      const av = safeNum(a[sortKey]);
      const bv = safeNum(b[sortKey]);
      // desempate por total pra ordenação ficar estável
      if (av === bv) return safeNum(b.total_tickets) - safeNum(a.total_tickets);
      return sortDir === "desc" ? bv - av : av - bv;
    });

    return arr;
  }, [usersWithLiveStatus, statusFilter, deferredSearch, sortKey, sortDir]);

  return (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardHeader
        sx={{ pb: 1 }}
        title="Ranking de atendentes"
        titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
        subheader={
          users.length === 0
            ? "Sem atendentes no período"
            : `${statusCounts.active} ativo${statusCounts.active !== 1 ? "s" : ""} de ${users.length}`
        }
        subheaderTypographyProps={{ variant: "caption" }}
      />

      <CardContent sx={{ pt: 0, pb: "0 !important", flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Busca + sort lado a lado, compacto */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
          <TextField
            size="small"
            placeholder="Buscar atendente"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              sx: { fontSize: 13 },
            }}
          />
          <Select
            size="small"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            sx={{ minWidth: 140, fontSize: 13 }}
          >
            {SORT_OPTIONS.map((opt) => (
              <MenuItem key={opt.key} value={opt.key} dense sx={{ fontSize: 13 }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <Tooltip title={sortDir === "desc" ? "Decrescente" : "Crescente"}>
            <IconButton
              size="small"
              onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              {sortDir === "desc" ? (
                <ArrowDownwardIcon fontSize="small" />
              ) : (
                <ArrowUpwardIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Filtro por status do atendente — segunda linha */}
        <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: "wrap", gap: 0.75 }}>
          {STATUS_OPTIONS.map((opt) => (
            <Chip
              key={opt.key}
              label={`${opt.label} ${statusCounts[opt.key] ?? 0}`}
              size="small"
              variant={statusFilter === opt.key ? "filled" : "outlined"}
              color={statusFilter === opt.key ? "primary" : "default"}
              onClick={() => setStatusFilter(opt.key)}
              sx={{ cursor: "pointer", height: 22, fontSize: 11 }}
            />
          ))}
        </Stack>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            mx: -1.5, // alinha hover dos rows com a borda do card
            px: 0,
            maxHeight: 440,
            minHeight: 280,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (theme) => theme.palette.divider,
              borderRadius: 3,
            },
          }}
        >
          {loading ? (
            <SkeletonRows />
          ) : visible.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {search || statusFilter !== "all"
                  ? "Nenhum atendente nesses filtros"
                  : "Sem atendentes no período"}
              </Typography>
              {(search || statusFilter !== "all") && (
                <Chip
                  label="Limpar"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("all");
                  }}
                  sx={{ mt: 1.5, cursor: "pointer", height: 22, fontSize: 11 }}
                />
              )}
            </Box>
          ) : (
            visible.map((user, i) => (
              <Row
                key={user.id}
                user={user}
                position={i + 1}
                isMe={currentUserId && user.id === currentUserId}
              />
            ))
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default React.memo(UserRanking);
